import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, Youtube, Instagram, Facebook } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";

const LangSelector = ({ dark }) => {
  const { lang, setLang } = useLang();
  return (
    <div className="flex items-center gap-2 text-[11px] tracking-[0.2em] font-medium" data-testid="language-selector" role="group" aria-label="Language / Lingua / Langue">
      {["it", "en", "fr"].map((l, i) => (
        <span key={l} className="flex items-center gap-2">
          {i > 0 && <span className={dark ? "text-[#F7F5F0]/30" : "text-[#1C1B18]/30"}>|</span>}
          <button
            data-testid={`lang-select-${l}`}
            onClick={() => setLang(l)}
            aria-pressed={lang === l}
            className={`uppercase transition-colors duration-300 hover:text-[#C5A059] ${
              lang === l ? "text-[#C5A059]" : dark ? "text-[#F7F5F0]/60" : "text-[#1C1B18]/60"
            }`}
          >
            {l}
          </button>
        </span>
      ))}
    </div>
  );
};

const Nav = () => {
  const { pathname } = useLocation();
  const { t } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  const NAV = [
    { to: "/", label: t("nav.home"), id: "home" },
    { to: "/#esplora", label: t("nav.esplora"), id: "esplora" },
    { to: "/esperienze", label: t("nav.esperienze"), id: "esperienze" },
    { to: "/echoes-from-above", label: t("nav.echoes"), id: "echoes" },
    { to: "/il-progetto", label: t("nav.progetto"), id: "progetto" },
    { to: "/eventi", label: t("nav.eventi"), id: "eventi" },
    { to: "/per-comuni-e-partner", label: t("nav.comuni"), id: "comuni" },
    { to: "/chi-sono", label: t("nav.chisono"), id: "chi-sono" },
    { to: "/contatti", label: t("nav.contatti"), id: "contatti" },
  ];

  const darkTop = pathname === "/" || pathname.startsWith("/borghi") || pathname === "/echoes-from-above" || pathname === "/esperienze";
  const solid = scrolled || !darkTop || open;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [pathname]);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    if (window.__lenis) open ? window.__lenis.stop() : window.__lenis.start();
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  return (
    <>
      <header
        data-testid="site-header"
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-700 ${
          solid ? "bg-[#F7F5F0]/95 backdrop-blur-md border-b border-[#1C1B18]/10 py-2.5" : "bg-transparent py-4"
        }`}
      >
        <div className="max-w-[1440px] mx-auto px-6 lg:px-10 flex items-center justify-between gap-6">
          <Link to="/" data-testid="header-logo" className="shrink-0" aria-label="Monferrato Immersive — Home">
            <img
              src={solid ? "/assets/logo-black.webp" : "/assets/logo-white.webp"}
              alt="Monferrato Immersive®"
              className="h-11 md:h-12 w-auto transition-opacity duration-500"
            />
          </Link>

          <nav className="hidden xl:flex items-center gap-5" aria-label="Navigazione principale">
            {NAV.map((item) => {
              const active = !item.to.includes("#") && pathname === item.to;
              return (
                <Link
                  key={item.id}
                  to={item.to}
                  data-testid={`nav-link-${item.id}`}
                  aria-current={active ? "page" : undefined}
                  className={`editorial-link text-[10px] tracking-[0.16em] uppercase font-medium transition-colors duration-300 whitespace-nowrap ${
                    active ? "text-[#C5A059]" : solid ? "text-[#1C1B18] hover:text-[#9E7B36]" : "text-[#F7F5F0] hover:text-[#D8B46C]"
                  }`}
                >
                  {item.label}
                </Link>
              );
            })}
          </nav>

          <div className="hidden xl:block">
            <LangSelector dark={!solid} />
          </div>

          <button
            data-testid="mobile-menu-button"
            onClick={() => setOpen(!open)}
            aria-label={open ? t("common.chiudiMenu") : t("common.apriMenu")}
            aria-expanded={open}
            className={`xl:hidden p-2 transition-colors ${solid ? "text-[#1C1B18]" : "text-[#F7F5F0]"}`}
          >
            {open ? <X size={24} strokeWidth={1.5} /> : <Menu size={24} strokeWidth={1.5} />}
          </button>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5 }}
            className="fixed inset-0 z-40 bg-[#0E0D0C] text-[#F7F5F0] flex flex-col justify-center px-8 pt-24"
          >
            <nav className="flex flex-col gap-1" aria-label="Menu mobile">
              {NAV.map((item, i) => (
                <motion.div
                  key={item.id}
                  initial={{ opacity: 0, x: -24 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.06 * i, duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
                >
                  <Link
                    to={item.to}
                    data-testid={`mobile-nav-link-${item.id}`}
                    className="font-serif-display text-2xl sm:text-3xl py-1.5 block hover:text-[#C5A059] transition-colors duration-300"
                  >
                    {item.label}
                  </Link>
                </motion.div>
              ))}
            </nav>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.6 }}
              className="mt-8 flex items-center justify-between border-t border-[#C5A059]/20 pt-6"
            >
              <LangSelector dark />
              <div className="flex gap-4 text-[#F7F5F0]/70">
                <a href="https://www.youtube.com/@MonferratoImmersive" target="_blank" rel="noopener noreferrer" aria-label="YouTube" data-testid="mobile-social-youtube"><Youtube size={18} /></a>
                <a href="https://www.instagram.com/monferratoimmersive" target="_blank" rel="noopener noreferrer" aria-label="Instagram" data-testid="mobile-social-instagram"><Instagram size={18} /></a>
                <a href="https://www.facebook.com/monferratoimmersive" target="_blank" rel="noopener noreferrer" aria-label="Facebook" data-testid="mobile-social-facebook"><Facebook size={18} /></a>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Nav;
